
import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { ModeToggle } from '@/components/mode-toggle'
import { Mascot, MascotVariant } from '@/components/shared/Mascot'
import { Logo } from '@/components/Logo'
import { isTauri } from '@/lib/utils/tauri'

export type AuthPageType =
  | 'login'
  | 'register'
  | 'login-confirmation'
  | 'register-confirmation'
  | 'forgot-password'
  | 'reset-password'

const MASCOT_HREF: Record<MascotVariant, string> = {
  welcome: '/characters/Welcome.svg',
  'welcome-alt': '/characters/Welcome (Alt).svg',
  'not-found': '/characters/Page Not Found.svg',
  error: '/characters/Something Went Wrong.svg',
  empty: '/characters/Empty States.svg',
  notifications: '/characters/Notifications.svg',
  award: '/characters/Award.svg',
  default: '/characters/Default.svg',
}

interface SplitAuthLayoutProps {
  children: ReactNode
  pageType?: AuthPageType
  customHeadline?: string
  customSubheadline?: string
}

interface PageContent {
  headline: string
  subheadline: string
  mascotVariant: MascotVariant
}

const PAGE_CONTENT: Record<AuthPageType, PageContent> = {
  login: {
    headline: 'Sign in.',
    subheadline:
      "Ready to crack the chaos? Let's get you signed in and on to what matters most.",
    mascotVariant: 'welcome',
  },
  register: {
    headline: 'Join the team.',
    subheadline:
      'Start organizing your work, collaborating with your team, and getting things done.',
    mascotVariant: 'welcome-alt',
  },
  'login-confirmation': {
    headline: 'Check your inbox.',
    subheadline:
      "We've sent you a verification code. Enter it below to continue.",
    mascotVariant: 'notifications',
  },
  'register-confirmation': {
    headline: 'Almost there.',
    subheadline:
      'Verify your email to complete your registration and start exploring.',
    mascotVariant: 'notifications',
  },
  'forgot-password': {
    headline: 'No worries.',
    subheadline: "We'll help you get back into your account in no time.",
    mascotVariant: 'default',
  },
  'reset-password': {
    headline: 'Create a new password.',
    subheadline: 'Choose a strong password to keep your account secure.',
    mascotVariant: 'award',
  },
}

export function SplitAuthLayout({
  children,
  pageType = 'login',
  customHeadline,
  customSubheadline,
}: SplitAuthLayoutProps) {
  const content = PAGE_CONTENT[pageType]
  const headline = customHeadline || content.headline
  const subheadline = customSubheadline || content.subheadline
  const showModeToggle = !isTauri()
  const year = new Date().getFullYear()

  const asideRef = useRef<HTMLElement>(null)
  const mascotRef = useRef<SVGGElement>(null)
  const bubbleRef = useRef<SVGGElement>(null)
  // Wall-clock hands — driven to the browser's local time
  const hourHandRef = useRef<SVGLineElement>(null)
  const minuteHandRef = useRef<SVGLineElement>(null)
  const secondHandRef = useRef<SVGLineElement>(null)
  // Parallax depth layers — far → near
  const pArtRef = useRef<SVGGElement>(null)
  const pBackRef = useRef<SVGGElement>(null)
  const pMascotRef = useRef<SVGGElement>(null)
  const pFrontRef = useRef<SVGGElement>(null)

  useEffect(() => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced) return

    const aside = asideRef.current
    const mascot = mascotRef.current
    if (!aside || !mascot) return

    // Each layer drifts by pointer × depth; nearer layers move more → 3D parallax.
    // Every plane drifts OPPOSITE the cursor — more the closer it is — so moving
    // the cursor reads as a camera gliding through a real room. The wall (window,
    // frames, clock) is the farthest plane, so it moves only a little; the desk
    // moves most. back + front share a depth so adjacent desk props never seam
    // apart; the mascot sits a touch shallower since it's tucked behind the monitor.
    // The wall plane (brick texture + mounted items) shares one depth: WALL_DX/DY.
    const WALL_DX = -4
    const WALL_DY = -3
    const layers: Array<{ el: SVGGElement | null; dx: number; dy: number }> = [
      { el: pArtRef.current, dx: WALL_DX, dy: WALL_DY },
      { el: pBackRef.current, dx: -14, dy: -10 },
      { el: pMascotRef.current, dx: -11, dy: -8 },
      { el: pFrontRef.current, dx: -14, dy: -10 },
    ]

    const state = { px: 0, py: 0, scale: 1, lean: 0, bubble: 0 }
    const target = { px: 0, py: 0, scale: 1, lean: 0, bubble: 0 }
    let rafId: number | null = null
    let running = false
    // Maps SVG user-units (viewBox 800×600, slice) → screen px, so the CSS brick
    // wall drifts exactly as far as the SVG wall items mounted on it.
    let wallScale = 1

    function apply() {
      for (const l of layers) {
        if (l.el)
          l.el.style.transform = `translate(${(state.px * l.dx).toFixed(2)}px, ${(
            state.py * l.dy
          ).toFixed(2)}px)`
      }
      if (aside) {
        aside.style.setProperty(
          '--brick-x',
          `${(state.px * WALL_DX * wallScale).toFixed(2)}px`,
        )
        aside.style.setProperty(
          '--brick-y',
          `${(state.py * WALL_DY * wallScale).toFixed(2)}px`,
        )
      }
      if (mascot)
        mascot.style.transform = `scale(${state.scale.toFixed(
          4,
        )}) rotate(${state.lean.toFixed(2)}deg)`
      if (bubbleRef.current)
        bubbleRef.current.style.opacity = state.bubble.toFixed(3)
    }

    function tick() {
      const k = 0.16
      state.px += (target.px - state.px) * k
      state.py += (target.py - state.py) * k
      state.scale += (target.scale - state.scale) * k
      state.lean += (target.lean - state.lean) * k
      state.bubble += (target.bubble - state.bubble) * k
      apply()
      const settled =
        Math.abs(target.px - state.px) < 0.001 &&
        Math.abs(target.py - state.py) < 0.001 &&
        Math.abs(target.scale - state.scale) < 0.0006 &&
        Math.abs(target.lean - state.lean) < 0.01 &&
        Math.abs(target.bubble - state.bubble) < 0.004
      if (settled) {
        running = false
        return
      }
      rafId = requestAnimationFrame(tick)
    }

    function start() {
      if (!running) {
        running = true
        rafId = requestAnimationFrame(tick)
      }
    }

    function handleMove(e: MouseEvent) {
      if (!mascot) return
      const rect = aside!.getBoundingClientRect()
      // viewBox 800×600 with "slice" → uniform cover scale
      wallScale = Math.max(rect.width / 800, rect.height / 600)
      target.px = Math.max(
        -1,
        Math.min(1, ((e.clientX - rect.left) / rect.width) * 2 - 1),
      )
      target.py = Math.max(
        -1,
        Math.min(1, ((e.clientY - rect.top) / rect.height) * 2 - 1),
      )
      const mr = mascot.getBoundingClientRect()
      const cx = mr.left + mr.width / 2
      const cy = mr.top + mr.height / 2
      const dist = Math.hypot(e.clientX - cx, e.clientY - cy)
      const proximity = 1 - Math.min(1, dist / 300)
      target.scale = 1 + proximity * 0.14
      // Lean toward the cursor — stronger the closer it gets.
      target.lean =
        Math.max(-1, Math.min(1, (e.clientX - cx) / 200)) *
        3.4 *
        (0.45 + 0.55 * proximity)
      target.bubble = proximity > 0.42 ? 1 : 0
      start()
    }

    function handleLeave() {
      target.px = 0
      target.py = 0
      target.scale = 1
      target.lean = 0
      target.bubble = 0
      start()
    }

    aside.addEventListener('mousemove', handleMove)
    aside.addEventListener('mouseleave', handleLeave)

    return () => {
      aside.removeEventListener('mousemove', handleMove)
      aside.removeEventListener('mouseleave', handleLeave)
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [])

  // Wall clock → browser local time
  useEffect(() => {
    const C = 408
    const Y = 150
    function update() {
      const now = new Date()
      const s = now.getSeconds() + now.getMilliseconds() / 1000
      const m = now.getMinutes() + s / 60
      const h = (now.getHours() % 12) + m / 60
      hourHandRef.current?.setAttribute(
        'transform',
        `rotate(${(h * 30).toFixed(2)} ${C} ${Y})`,
      )
      minuteHandRef.current?.setAttribute(
        'transform',
        `rotate(${(m * 6).toFixed(2)} ${C} ${Y})`,
      )
      secondHandRef.current?.setAttribute(
        'transform',
        `rotate(${(s * 6).toFixed(2)} ${C} ${Y})`,
      )
    }
    update()
    const id = window.setInterval(update, 1000)
    return () => window.clearInterval(id)
  }, [])

  return (
    <div className="relative flex h-[100svh] overflow-hidden bg-background">
      {/* ───────────────── LEFT — Brand stage ───────────────── */}
      <aside
        ref={asideRef}
        className="relative hidden overflow-hidden lg:flex lg:w-1/2 xl:w-[55%]"
      >
        {/* Office wall + daylight — light */}
        <div
          aria-hidden
          className="absolute inset-0 dark:hidden"
          style={{
            background: `
              radial-gradient(ellipse 55% 50% at 85% 8%, hsl(38 85% 78% / 0.40), transparent 65%),
              radial-gradient(ellipse 60% 45% at 95% 28%, hsl(35 75% 82% / 0.28), transparent 70%),
              radial-gradient(ellipse 45% 60% at 10% 95%, hsl(220 18% 80% / 0.22), transparent 65%),
              radial-gradient(ellipse 80% 25% at 50% 100%, hsl(30 12% 80% / 0.30), transparent 75%),
              linear-gradient(180deg, hsl(36 26% 95%) 0%, hsl(34 22% 92%) 55%, hsl(32 18% 88%) 100%)
            `,
          }}
        />
        {/* Office wall + daylight — dark */}
        <div
          aria-hidden
          className="absolute inset-0 hidden dark:block"
          style={{
            background: `
              radial-gradient(ellipse 55% 50% at 85% 8%, hsl(38 60% 60% / 0.22), transparent 65%),
              radial-gradient(ellipse 60% 45% at 95% 28%, hsl(35 50% 55% / 0.14), transparent 70%),
              radial-gradient(ellipse 45% 60% at 10% 95%, hsl(220 18% 22% / 0.40), transparent 65%),
              linear-gradient(180deg, hsl(28 12% 10%) 0%, hsl(26 10% 8%) 60%, hsl(24 8% 6%) 100%)
            `,
          }}
        />

        {/* White brick wall — light (moves with the wall-mounted items) */}
        <div
          aria-hidden
          className="pointer-events-none absolute opacity-[0.85] dark:hidden"
          style={{
            inset: '-32px',
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='280' height='100' viewBox='0 0 280 100'><rect width='280' height='100' fill='%23dcd5c9'/><rect x='1' y='1' width='138' height='48' rx='2' fill='%23f7f1e7'/><rect x='141' y='1' width='138' height='48' rx='2' fill='%23ece5d9'/><rect x='-69' y='51' width='138' height='48' rx='2' fill='%23f2ecde'/><rect x='71' y='51' width='138' height='48' rx='2' fill='%23f7f1e7'/><rect x='211' y='51' width='138' height='48' rx='2' fill='%23ece5d9'/></svg>\")",
            backgroundSize: '280px 100px',
            backgroundRepeat: 'repeat',
            mixBlendMode: 'multiply',
            transform: 'translate(var(--brick-x, 0px), var(--brick-y, 0px))',
            willChange: 'transform',
          }}
        />
        {/* White brick wall — dark */}
        <div
          aria-hidden
          className="pointer-events-none absolute hidden opacity-90 dark:block"
          style={{
            inset: '-32px',
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='280' height='100' viewBox='0 0 280 100'><rect width='280' height='100' fill='%230d0c0b'/><rect x='1' y='1' width='138' height='48' rx='2' fill='%23252220'/><rect x='141' y='1' width='138' height='48' rx='2' fill='%231d1b1a'/><rect x='-69' y='51' width='138' height='48' rx='2' fill='%23211f1d'/><rect x='71' y='51' width='138' height='48' rx='2' fill='%23252220'/><rect x='211' y='51' width='138' height='48' rx='2' fill='%231d1b1a'/></svg>\")",
            backgroundSize: '280px 100px',
            backgroundRepeat: 'repeat',
            transform: 'translate(var(--brick-x, 0px), var(--brick-y, 0px))',
            willChange: 'transform',
          }}
        />

        {/* Grain texture — barely there */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.35] mix-blend-soft-light dark:opacity-[0.5]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.55 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
          }}
        />

        {/* Soft pool of daylight on the desk area */}
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 z-[1] h-[70%]"
          style={{
            background:
              'radial-gradient(ellipse 50% 45% at 55% 70%, hsl(40 70% 85% / 0.30), transparent 72%)',
          }}
        />

        {/* ─── Workspace scene — single SVG, mascot locked to monitor ─── */}
        <div className="absolute inset-0 z-[2] overflow-hidden">
          <svg
            className="h-full w-full"
            viewBox="0 0 800 600"
            preserveAspectRatio="xMidYMax slice"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <defs>
              <linearGradient id="leafGrad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--brand-green))" stopOpacity="0.55" />
                <stop offset="100%" stopColor="hsl(var(--brand-green))" stopOpacity="0.30" />
              </linearGradient>
              <linearGradient id="leafGrad2" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(150 70% 38%)" stopOpacity="0.50" />
                <stop offset="100%" stopColor="hsl(150 70% 38%)" stopOpacity="0.25" />
              </linearGradient>
              <linearGradient id="deskGrad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(28 22% 36%)" />
                <stop offset="100%" stopColor="hsl(26 24% 26%)" />
              </linearGradient>
              <linearGradient id="deskGradDark" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(26 14% 12%)" />
                <stop offset="100%" stopColor="hsl(24 14% 7%)" />
              </linearGradient>
              <linearGradient id="monitorGrad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(220 14% 26%)" />
                <stop offset="100%" stopColor="hsl(220 14% 18%)" />
              </linearGradient>
              <linearGradient id="screenGrad" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(220 60% 92%)" />
                <stop offset="100%" stopColor="hsl(150 50% 92%)" />
              </linearGradient>
              <linearGradient id="screenGradDark" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(220 28% 18%)" />
                <stop offset="100%" stopColor="hsl(150 24% 16%)" />
              </linearGradient>
              <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="6" stdDeviation="6" floodOpacity="0.10" />
              </filter>
              <filter id="frameShadow" x="-15%" y="-15%" width="130%" height="130%">
                <feDropShadow dx="0" dy="8" stdDeviation="5" floodOpacity="0.16" />
              </filter>
              <linearGradient id="artSky" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(38 50% 94%)" />
                <stop offset="100%" stopColor="hsl(20 60% 86%)" />
              </linearGradient>
              <linearGradient id="artSkyDark" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(28 30% 26%)" />
                <stop offset="100%" stopColor="hsl(18 35% 22%)" />
              </linearGradient>
              {/* Window sky — daytime / dusk */}
              <linearGradient id="windowSky" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(205 75% 80%)" />
                <stop offset="60%" stopColor="hsl(200 70% 88%)" />
                <stop offset="100%" stopColor="hsl(40 75% 92%)" />
              </linearGradient>
              <linearGradient id="windowSkyDark" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(225 38% 20%)" />
                <stop offset="70%" stopColor="hsl(222 34% 26%)" />
                <stop offset="100%" stopColor="hsl(28 40% 30%)" />
              </linearGradient>
              {/* Volumetric daylight beam from the window */}
              <linearGradient id="godRay" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(42 90% 88%)" stopOpacity="0.55" />
                <stop offset="55%" stopColor="hsl(40 85% 84%)" stopOpacity="0.16" />
                <stop offset="100%" stopColor="hsl(40 85% 84%)" stopOpacity="0" />
              </linearGradient>
              {/* Warm pool of light around the lamp / desk */}
              <radialGradient id="lampGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="hsl(44 95% 72%)" stopOpacity="0.85" />
                <stop offset="45%" stopColor="hsl(40 90% 66%)" stopOpacity="0.35" />
                <stop offset="100%" stopColor="hsl(38 90% 60%)" stopOpacity="0" />
              </radialGradient>
              {/* Cool glow spilling off the monitor screen */}
              <radialGradient id="screenSpill" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="hsl(170 70% 78%)" stopOpacity="0.5" />
                <stop offset="100%" stopColor="hsl(190 70% 70%)" stopOpacity="0" />
              </radialGradient>
              {/* Contact-shadow falloff under props */}
              <radialGradient id="contact" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="hsl(28 30% 8%)" stopOpacity="0.28" />
                <stop offset="100%" stopColor="hsl(28 30% 8%)" stopOpacity="0" />
              </radialGradient>
              <filter id="bubbleShadow" x="-40%" y="-40%" width="180%" height="180%">
                <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.18" />
              </filter>
            </defs>

            {/* ── WALL ART ── (hung on the wall, behind everything else) */}
            <g ref={pArtRef} style={{ willChange: 'transform' }}>

            {/* Frame 1 — minimalist sun + horizon, landscape, left */}
            <g filter="url(#frameShadow)">
              {/* Frame border */}
              <rect x="78" y="108" width="184" height="138" rx="2" className="fill-[hsl(28,12%,20%)] dark:fill-[hsl(28,8%,8%)]" />
              {/* Mat */}
              <rect x="84" y="114" width="172" height="126" className="fill-[hsl(40,30%,96%)] dark:fill-[hsl(30,10%,16%)]" />
              {/* Art canvas — sky gradient */}
              <rect x="94" y="124" width="152" height="106" className="fill-[url(#artSky)] dark:fill-[url(#artSkyDark)]" />
              {/* Sun */}
              <circle cx="198" cy="154" r="14" fill="hsl(var(--brand-orange))" opacity="0.88" />
              {/* Foreground field */}
              <rect x="94" y="206" width="152" height="24" fill="hsl(var(--brand-green))" opacity="0.55" />
              {/* Horizon ridge */}
              <path d="M94 206 L130 192 L156 202 L188 188 L218 198 L246 190 L246 206 Z" fill="hsl(var(--brand-green))" opacity="0.75" />
            </g>

            {/* Office window — daylight + skyline (motivates the god-ray) */}
            <g filter="url(#frameShadow)">
              {/* Outer frame */}
              <rect x="552" y="92" width="156" height="158" rx="4" className="fill-[hsl(32,18%,86%)] dark:fill-[hsl(28,10%,16%)]" />
              {/* Glass */}
              <rect x="564" y="104" width="132" height="134" className="fill-[url(#windowSky)] dark:fill-[url(#windowSkyDark)]" />
              {/* Sun glow in the sky */}
              <circle cx="660" cy="138" r="13" fill="hsl(var(--brand-yellow))" opacity="0.85" className="dark:opacity-30" />
              {/* Drifting clouds */}
              <g fill="hsl(0 0% 100%)" opacity="0.78" className="dark:opacity-20">
                <g>
                  <animateTransform attributeName="transform" type="translate" values="-6 0; 6 0; -6 0" dur="22s" repeatCount="indefinite" />
                  <ellipse cx="598" cy="128" rx="15" ry="6" />
                  <ellipse cx="610" cy="125" rx="11" ry="6" />
                </g>
                <g>
                  <animateTransform attributeName="transform" type="translate" values="5 0; -5 0; 5 0" dur="28s" repeatCount="indefinite" />
                  <ellipse cx="648" cy="160" rx="12" ry="5" />
                  <ellipse cx="638" cy="162" rx="9" ry="5" />
                </g>
              </g>
              {/* City skyline silhouette along the horizon */}
              <g className="fill-[hsl(212,28%,62%)] dark:fill-[hsl(220,22%,12%)]" opacity="0.9">
                <rect x="564" y="196" width="20" height="42" />
                <rect x="586" y="178" width="16" height="60" />
                <rect x="604" y="206" width="14" height="32" />
                <rect x="620" y="186" width="20" height="52" />
                <rect x="642" y="200" width="12" height="38" />
                <rect x="656" y="172" width="18" height="66" />
                <rect x="676" y="194" width="20" height="44" />
              </g>
              {/* A few lit windows in the towers */}
              <g fill="hsl(var(--brand-yellow))" opacity="0.85">
                <rect x="590" y="186" width="3" height="3" />
                <rect x="596" y="186" width="3" height="3" />
                <rect x="590" y="194" width="3" height="3" />
                <rect x="625" y="194" width="3" height="3" />
                <rect x="631" y="200" width="3" height="3" />
                <rect x="661" y="182" width="3" height="3" />
                <rect x="667" y="190" width="3" height="3" />
                <rect x="661" y="198" width="3" height="3" />
              </g>
              {/* Mullions (window cross) */}
              <rect x="626" y="104" width="8" height="134" className="fill-[hsl(32,18%,86%)] dark:fill-[hsl(28,10%,16%)]" />
              <rect x="564" y="166" width="132" height="8" className="fill-[hsl(32,18%,86%)] dark:fill-[hsl(28,10%,16%)]" />
              {/* Sill */}
              <rect x="546" y="246" width="168" height="9" rx="2" className="fill-[hsl(32,16%,80%)] dark:fill-[hsl(28,10%,13%)]" />
            </g>

            {/* Wall clock — sweeping second hand */}
            <g filter="url(#frameShadow)">
              <circle cx="408" cy="150" r="33" className="fill-[hsl(34,20%,90%)] dark:fill-[hsl(28,10%,18%)]" />
              <circle cx="408" cy="150" r="27" className="fill-[hsl(40,30%,97%)] dark:fill-[hsl(30,8%,12%)]" />
              {/* hour ticks */}
              <g className="fill-foreground/40">
                <rect x="406.5" y="126" width="3" height="5" rx="1.5" />
                <rect x="406.5" y="169" width="3" height="5" rx="1.5" />
                <rect x="384" y="148.5" width="5" height="3" rx="1.5" />
                <rect x="427" y="148.5" width="5" height="3" rx="1.5" />
              </g>
              {/* hands point to 12; JS rotates them to the browser's local time */}
              <line ref={hourHandRef} x1="408" y1="150" x2="408" y2="133" className="stroke-foreground/70" strokeWidth="2.6" strokeLinecap="round" />
              <line ref={minuteHandRef} x1="408" y1="150" x2="408" y2="126" className="stroke-foreground/70" strokeWidth="2.2" strokeLinecap="round" />
              <line ref={secondHandRef} x1="408" y1="153" x2="408" y2="125" stroke="hsl(var(--brand-red))" strokeWidth="1.2" strokeLinecap="round" />
              <circle cx="408" cy="150" r="2.4" className="fill-foreground/80" />
            </g>
            </g>

            {/* ── ATMOSPHERE — volumetric beam + drifting dust ── */}
            <g aria-hidden style={{ mixBlendMode: 'screen' }}>
              {/* God-ray streaming out of the window, gently breathing */}
              <polygon points="584,150 690,150 556,566 312,566" fill="url(#godRay)" opacity="0.5">
                <animate attributeName="opacity" values="0.40; 0.62; 0.40" dur="11s" repeatCount="indefinite" />
              </polygon>
              {/* Dust motes caught in the light */}
              <g fill="hsl(45 80% 92%)">
                <circle cx="430" cy="300" r="1.6">
                  <animate attributeName="cy" values="300;180" dur="13s" repeatCount="indefinite" />
                  <animate attributeName="cx" values="430;414" dur="13s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0;0.9;0.9;0" dur="13s" repeatCount="indefinite" />
                </circle>
                <circle cx="520" cy="360" r="1.2">
                  <animate attributeName="cy" values="360;150" dur="16s" repeatCount="indefinite" begin="-4s" />
                  <animate attributeName="cx" values="520;500" dur="16s" repeatCount="indefinite" begin="-4s" />
                  <animate attributeName="opacity" values="0;0.8;0.8;0" dur="16s" repeatCount="indefinite" begin="-4s" />
                </circle>
                <circle cx="600" cy="280" r="1.8">
                  <animate attributeName="cy" values="280;120" dur="14s" repeatCount="indefinite" begin="-7s" />
                  <animate attributeName="cx" values="600;588" dur="14s" repeatCount="indefinite" begin="-7s" />
                  <animate attributeName="opacity" values="0;1;1;0" dur="14s" repeatCount="indefinite" begin="-7s" />
                </circle>
                <circle cx="470" cy="420" r="1.3">
                  <animate attributeName="cy" values="420;220" dur="18s" repeatCount="indefinite" begin="-2s" />
                  <animate attributeName="cx" values="470;452" dur="18s" repeatCount="indefinite" begin="-2s" />
                  <animate attributeName="opacity" values="0;0.7;0.7;0" dur="18s" repeatCount="indefinite" begin="-2s" />
                </circle>
                <circle cx="560" cy="240" r="1.1">
                  <animate attributeName="cy" values="240;110" dur="12s" repeatCount="indefinite" begin="-9s" />
                  <animate attributeName="cx" values="560;548" dur="12s" repeatCount="indefinite" begin="-9s" />
                  <animate attributeName="opacity" values="0;0.85;0.85;0" dur="12s" repeatCount="indefinite" begin="-9s" />
                </circle>
                <circle cx="380" cy="360" r="1.4">
                  <animate attributeName="cy" values="360;200" dur="15s" repeatCount="indefinite" begin="-11s" />
                  <animate attributeName="cx" values="380;366" dur="15s" repeatCount="indefinite" begin="-11s" />
                  <animate attributeName="opacity" values="0;0.75;0.75;0" dur="15s" repeatCount="indefinite" begin="-11s" />
                </circle>
              </g>
            </g>

            {/* ── BACK LAYER ── (deep props: lamp arm, glow pools, contact shadow) */}
            <g ref={pBackRef} style={{ willChange: 'transform' }}>

            {/* Warm pool of lamplight on the desk, pulsing with the bulb */}
            <ellipse cx="613" cy="476" rx="104" ry="62" fill="url(#lampGlow)" style={{ mixBlendMode: 'screen' }} opacity="0.5">
              <animate attributeName="opacity" values="0.40; 0.60; 0.40" dur="3.4s" repeatCount="indefinite" />
            </ellipse>
            {/* Cool spill from the monitor onto the mascot/desk */}
            <ellipse cx="400" cy="452" rx="86" ry="54" fill="url(#screenSpill)" style={{ mixBlendMode: 'screen' }} opacity="0.45" />
            {/* Soft contact shadow grounding the mascot */}
            <ellipse cx="400" cy="500" rx="74" ry="11" fill="url(#contact)" />

            {/* Plant — stem 1 (sways gently) */}
            <g>
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0 178 476; 1.5 178 476; 0 178 476; -1.5 178 476; 0 178 476"
                dur="6.5s"
                repeatCount="indefinite"
              />
              <path d="M178 476 Q156 432 168 386" stroke="hsl(var(--brand-green))" strokeOpacity="0.7" strokeWidth="3.5" fill="none" strokeLinecap="round" />
              <path d="M168 386 Q150 372 142 384 Q150 398 168 392 Z" fill="url(#leafGrad)" />
              <path d="M168 386 Q186 374 196 386 Q186 402 170 396 Z" fill="url(#leafGrad2)" />
            </g>

            {/* Plant — stem 2 */}
            <g>
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0 186 476; -1.5 186 476; 0 186 476; 1.5 186 476; 0 186 476"
                dur="7.5s"
                repeatCount="indefinite"
                begin="0.4s"
              />
              <path d="M186 476 Q206 430 196 384" stroke="hsl(var(--brand-green))" strokeOpacity="0.65" strokeWidth="3" fill="none" strokeLinecap="round" />
              <path d="M196 384 Q210 372 220 386 Q210 402 196 394 Z" fill="url(#leafGrad)" />
            </g>

            {/* Plant — stem 3 */}
            <g>
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0 174 476; 1 174 476; 0 174 476; -1 174 476; 0 174 476"
                dur="5.5s"
                repeatCount="indefinite"
                begin="1.1s"
              />
              <path d="M174 476 Q146 446 152 414" stroke="hsl(var(--brand-green))" strokeOpacity="0.55" strokeWidth="2.5" fill="none" strokeLinecap="round" />
              <path d="M152 414 Q140 404 134 416 Q142 426 154 420 Z" fill="url(#leafGrad2)" />
            </g>

            {/* Coffee steam — slow, subtle, short wisps */}
            {/* Wisp A — left */}
            <g transform="translate(530 458)">
              <g>
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  values="0 0; -2 -12; 1 -22; 0 -30"
                  keyTimes="0; 0.35; 0.7; 1"
                  dur="6.5s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0; 0.28; 0.22; 0"
                  keyTimes="0; 0.25; 0.78; 1"
                  dur="6.5s"
                  repeatCount="indefinite"
                />
                <path
                  d="M0 0 q-3 -3 0 -6 q3 -3 0 -6 q-3 -3 0 -6"
                  stroke="hsl(var(--foreground))"
                  strokeWidth="1.6"
                  fill="none"
                  strokeLinecap="round"
                />
              </g>
            </g>

            {/* Wisp B — center */}
            <g transform="translate(537 458)">
              <g>
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  values="0 0; 1 -14; -1 -26; 0 -34"
                  keyTimes="0; 0.35; 0.7; 1"
                  dur="7s"
                  repeatCount="indefinite"
                  begin="-2.3s"
                />
                <animate
                  attributeName="opacity"
                  values="0; 0.30; 0.22; 0"
                  keyTimes="0; 0.25; 0.78; 1"
                  dur="7s"
                  repeatCount="indefinite"
                  begin="-2.3s"
                />
                <path
                  d="M0 0 q3 -3 0 -6 q-3 -3 0 -6 q3 -3 0 -6 q-3 -3 0 -6"
                  stroke="hsl(var(--foreground))"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </g>
            </g>

            {/* Wisp C — right */}
            <g transform="translate(544 458)">
              <g>
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  values="0 0; -1 -11; 2 -20; 0 -28"
                  keyTimes="0; 0.35; 0.7; 1"
                  dur="6s"
                  repeatCount="indefinite"
                  begin="-4s"
                />
                <animate
                  attributeName="opacity"
                  values="0; 0.24; 0.18; 0"
                  keyTimes="0; 0.25; 0.78; 1"
                  dur="6s"
                  repeatCount="indefinite"
                  begin="-4s"
                />
                <path
                  d="M0 0 q-2 -3 0 -6 q2 -3 0 -6 q-2 -3 0 -6"
                  stroke="hsl(var(--foreground))"
                  strokeWidth="1.4"
                  fill="none"
                  strokeLinecap="round"
                />
              </g>
            </g>

            {/* Desk-lamp arm (behind monitor edge) — base sits on desk top */}
            <g transform="translate(-55 0)">
              <path d="M690 491 L690 406 Q690 384 670 383" stroke="hsl(var(--foreground))" strokeOpacity="0.35" strokeWidth="3" fill="none" strokeLinecap="round" />
              <ellipse cx="690" cy="494" rx="14" ry="6" className="fill-foreground/30" />
            </g>
            </g>

            {/* ── MASCOT PLANE ── own parallax depth; leans + pops toward the cursor */}
            <g ref={pMascotRef} style={{ willChange: 'transform' }}>

            {/* Thought bubble — fades in as the cursor approaches the mascot */}
            <g
              ref={bubbleRef}
              style={{ opacity: 0, willChange: 'opacity' }}
              filter="url(#bubbleShadow)"
            >
              <g>
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  values="0 0; 0 -3; 0 0"
                  dur="3.6s"
                  repeatCount="indefinite"
                />
                {/* tail */}
                <circle cx="378" cy="280" r="4.5" className="fill-[hsl(40,40%,98%)] dark:fill-[hsl(28,12%,18%)]" />
                <circle cx="389" cy="291" r="3" className="fill-[hsl(40,40%,98%)] dark:fill-[hsl(28,12%,18%)]" />
                {/* body */}
                <rect x="296" y="230" width="146" height="46" rx="13" className="fill-[hsl(40,40%,98%)] dark:fill-[hsl(28,12%,18%)]" />
                {/* a little spark of inspiration */}
                <path
                  d="M318 250 l3.2 -7 l3.2 7 l7 3.2 l-7 3.2 l-3.2 7 l-3.2 -7 l-7 -3.2 z"
                  fill="hsl(var(--brand-yellow))"
                >
                  <animateTransform attributeName="transform" type="rotate" values="0 321 256; 12 321 256; 0 321 256; -12 321 256; 0 321 256" dur="4s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.7;1;0.7" dur="2.2s" repeatCount="indefinite" />
                </path>
                {/* greeting */}
                <text
                  x="338"
                  y="258"
                  fontSize="14"
                  fontWeight="700"
                  className="fill-foreground font-heading"
                >
                  {"Hi, I'm Benny!"}
                </text>
              </g>
            </g>

            {/* Mascot — locked to monitor; subtle bob + scale/lean driven in JS */}
            <g
              ref={mascotRef}
              style={{
                transformBox: 'fill-box',
                transformOrigin: '50% 100%',
              }}
            >
              <g>
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  values="0 0; 0 -3; 0 0"
                  dur="5s"
                  repeatCount="indefinite"
                />
                <image
                  href={MASCOT_HREF[content.mascotVariant]}
                  x="320"
                  y="282"
                  width="160"
                  height="220"
                  preserveAspectRatio="xMidYMax meet"
                />
              </g>
            </g>
            </g>

            {/* ── FRONT LAYER — desk surface + props, renders on top of mascot ── */}
            <g ref={pFrontRef} style={{ willChange: 'transform' }}>

            {/* Plant pot */}
            <g filter="url(#softShadow)">
              <path
                d="M158 476 L202 476 L196 504 Q180 510 164 504 Z"
                className="fill-[hsl(25,40%,72%)] dark:fill-[hsl(25,30%,32%)]"
              />
              <ellipse cx="180" cy="476" rx="22" ry="5" className="fill-[hsl(25,40%,66%)] dark:fill-[hsl(25,30%,26%)]" />
            </g>

            {/* Book stack */}
            <g filter="url(#softShadow)">
              <rect x="226" y="488" width="58" height="14" rx="2" fill="hsl(var(--brand-blue))" opacity="0.85" />
              <rect x="232" y="474" width="50" height="14" rx="2" fill="hsl(var(--brand-orange))" opacity="0.85" />
              <rect x="228" y="460" width="54" height="14" rx="2" fill="hsl(var(--brand-green))" opacity="0.85" />
              <line x1="234" y1="467" x2="280" y2="467" stroke="white" strokeOpacity="0.35" strokeWidth="1" />
              <line x1="238" y1="481" x2="280" y2="481" stroke="white" strokeOpacity="0.35" strokeWidth="1" />
              <line x1="232" y1="495" x2="282" y2="495" stroke="white" strokeOpacity="0.35" strokeWidth="1" />
            </g>

            {/* Monitor */}
            <g filter="url(#softShadow)">
              <rect x="394" y="488" width="12" height="14" className="fill-[hsl(220,10%,38%)] dark:fill-[hsl(220,8%,32%)]" />
              <rect x="370" y="500" width="60" height="6" rx="3" className="fill-[hsl(220,10%,38%)] dark:fill-[hsl(220,8%,32%)]" />
              <rect x="328" y="376" width="144" height="114" rx="10" className="fill-[url(#monitorGrad)]" />
              <rect x="336" y="384" width="128" height="92" rx="4" className="fill-[url(#screenGrad)] dark:fill-[url(#screenGradDark)]" />
              <rect x="342" y="390" width="116" height="10" rx="2" className="fill-foreground/15" />
              <circle cx="349" cy="395" r="2" fill="hsl(var(--brand-red))" opacity="0.7" />
              <circle cx="356" cy="395" r="2" fill="hsl(var(--brand-yellow))" opacity="0.85" />
              <circle cx="363" cy="395" r="2" fill="hsl(var(--brand-green))" opacity="0.85" />
              <rect x="342" y="404" width="22" height="66" rx="2" className="fill-foreground/10" />
              <rect x="346" y="408" width="14" height="3" rx="1" className="fill-foreground/30" />
              <rect x="346" y="414" width="10" height="3" rx="1" className="fill-foreground/22" />
              <rect x="346" y="420" width="12" height="3" rx="1" className="fill-foreground/22" />
              <rect x="346" y="426" width="9" height="3" rx="1" className="fill-foreground/22" />
              <rect x="368" y="406" width="90" height="6" rx="2" className="fill-foreground/25" />
              <rect x="368" y="416" width="50" height="4" rx="2" className="fill-foreground/15" />
              {/* blinking text caret */}
              <rect x="421" y="404" width="2" height="9" className="fill-foreground/55">
                <animate attributeName="opacity" values="1;1;0;0" keyTimes="0;0.5;0.5;1" dur="1.1s" repeatCount="indefinite" />
              </rect>
              {/* progress card — bar fills on a loop */}
              <rect x="368" y="424" width="90" height="14" rx="3" fill="hsl(var(--brand-green))" opacity="0.16" />
              <rect x="372" y="429" width="10" height="4" rx="2" fill="hsl(var(--brand-green))">
                <animate attributeName="width" values="10;46;46;10" keyTimes="0;0.5;0.85;1" dur="4.6s" repeatCount="indefinite" />
              </rect>
              {/* task card — checkbox completes on a loop */}
              <rect x="368" y="444" width="90" height="14" rx="3" fill="hsl(var(--brand-blue))" opacity="0.14" />
              <rect x="372" y="447" width="8" height="8" rx="2" fill="hsl(var(--brand-blue))" opacity="0.3">
                <animate attributeName="opacity" values="0.3;0.3;0.95;0.95;0.3" keyTimes="0;0.45;0.5;0.92;1" dur="4.6s" repeatCount="indefinite" />
              </rect>
              <polyline points="373,451 376,454 380,449" fill="none" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="11" strokeDashoffset="11">
                <animate attributeName="stroke-dashoffset" values="11;11;0;0;11" keyTimes="0;0.45;0.62;0.92;1" dur="4.6s" repeatCount="indefinite" />
              </polyline>
              <rect x="388" y="449" width="36" height="4" rx="2" fill="hsl(var(--brand-blue))" opacity="0.55" />
              {/* animated sparkline */}
              <polyline
                points="368,470 380,466 392,469 404,461 416,464 428,456 440,460 452,452"
                fill="none"
                stroke="hsl(var(--brand-orange))"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="130"
                strokeDashoffset="130"
              >
                <animate attributeName="stroke-dashoffset" values="130;130;0;0;130" keyTimes="0;0.05;0.45;0.9;1" dur="9s" repeatCount="indefinite" />
              </polyline>
            </g>

            {/* Coffee mug — sits on desk top */}
            <g filter="url(#softShadow)" transform="translate(-55 0)">
              <rect x="568" y="460" width="46" height="40" rx="5" className="fill-[hsl(0,0%,96%)] dark:fill-[hsl(0,0%,82%)]" />
              <rect x="568" y="460" width="46" height="6" className="fill-[hsl(0,0%,88%)] dark:fill-[hsl(0,0%,74%)]" />
              <rect x="568" y="476" width="46" height="4" fill="hsl(var(--brand-green))" opacity="0.85" />
              <path d="M614 470 Q632 474 632 484 Q632 494 614 496" stroke="hsl(0,0%,88%)" strokeWidth="4" fill="none" strokeLinecap="round" className="dark:stroke-[hsl(0,0%,74%)]" />
              <ellipse cx="591" cy="462" rx="20" ry="3.5" className="fill-[hsl(25,55%,28%)]" opacity="0.7" />
            </g>

            {/* Desk lamp head — shade flares open toward the desk, bulb glows */}
            <g filter="url(#softShadow)" transform="translate(-55 0)">
              <path d="M662 382 L678 382 L688 404 L652 404 Z" className="fill-[hsl(220,12%,40%)] dark:fill-[hsl(220,10%,30%)]" />
              <ellipse cx="670" cy="404" rx="18" ry="3" className="fill-[hsl(220,12%,32%)] dark:fill-[hsl(220,10%,22%)]" />
              <circle cx="670" cy="403" r="4" fill="hsl(var(--brand-yellow))">
                <animate attributeName="opacity" values="0.75; 1; 0.75" dur="3.4s" repeatCount="indefinite" />
              </circle>
            </g>

            {/* Desk top */}
            <rect x="80" y="500" width="640" height="22" rx="4" className="fill-[url(#deskGrad)] dark:fill-[url(#deskGradDark)]" />
            <rect x="80" y="500" width="640" height="3" className="fill-white/35 dark:fill-white/10" />

            {/* Keyboard + mouse — resting on the desk surface */}
            <g filter="url(#softShadow)">
              <rect x="350" y="503" width="108" height="13" rx="2.5" className="fill-[hsl(220,8%,84%)] dark:fill-[hsl(220,7%,30%)]" />
              <rect x="350" y="503" width="108" height="3" rx="2" className="fill-white/40 dark:fill-white/10" />
              {/* key rows */}
              <g className="fill-foreground/15">
                <rect x="355" y="507" width="6" height="2.4" rx="1" />
                <rect x="364" y="507" width="6" height="2.4" rx="1" />
                <rect x="373" y="507" width="6" height="2.4" rx="1" />
                <rect x="382" y="507" width="6" height="2.4" rx="1" />
                <rect x="391" y="507" width="6" height="2.4" rx="1" />
                <rect x="400" y="507" width="6" height="2.4" rx="1" />
                <rect x="409" y="507" width="6" height="2.4" rx="1" />
                <rect x="418" y="507" width="6" height="2.4" rx="1" />
                <rect x="427" y="507" width="6" height="2.4" rx="1" />
                <rect x="436" y="507" width="6" height="2.4" rx="1" />
                <rect x="445" y="507" width="8" height="2.4" rx="1" />
                <rect x="378" y="511" width="52" height="2.6" rx="1.3" />
              </g>
              {/* mouse */}
              <rect x="466" y="504" width="15" height="12" rx="6" className="fill-[hsl(220,8%,84%)] dark:fill-[hsl(220,7%,30%)]" />
              <line x1="473.5" y1="505" x2="473.5" y2="509" className="stroke-foreground/20" strokeWidth="1" strokeLinecap="round" />
            </g>

            {/* Desk legs */}
            <rect x="130" y="522" width="14" height="78" className="fill-[hsl(28,22%,22%)] dark:fill-[hsl(26,12%,6%)]" />
            <rect x="656" y="522" width="14" height="78" className="fill-[hsl(28,22%,22%)] dark:fill-[hsl(26,12%,6%)]" />
            </g>
          </svg>
        </div>

        {/* Cinematic vignette — deepens the corners, focuses the desk */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-[3]"
          style={{
            background:
              'radial-gradient(ellipse 78% 72% at 52% 46%, transparent 52%, hsl(28 24% 10% / 0.12) 100%)',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-[3] hidden dark:block"
          style={{
            background:
              'radial-gradient(ellipse 78% 72% at 52% 46%, transparent 46%, hsl(24 30% 2% / 0.45) 100%)',
          }}
        />

        {/* Content overlay (logo + tagline + footer) */}
        <div className="relative z-10 flex w-full flex-col justify-between px-6 pb-6 pt-4 lg:px-8 lg:pb-8 lg:pt-5 xl:px-12 xl:pb-12 xl:pt-6">
          {/* Top — Logo (→ scrambl.com) */}
          <div className="flex h-10 items-center">
            <a
              href="https://scrambl.com"
              className="inline-block transition-opacity hover:opacity-80"
            >
              <Logo
                size={28}
                withText
                textClassName="text-2xl font-light tracking-wide font-kronaone"
              />
            </a>
          </div>

          <div className="flex-1" />

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <p>&copy; {year} Scrambl, LLC</p>
            <div className="flex items-center gap-4">
              <a
                href="https://scrambl.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors hover:text-foreground"
              >
                Terms
              </a>
              <a
                href="https://scrambl.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors hover:text-foreground"
              >
                Privacy
              </a>
              <a
                href="https://scrambl.com/eula"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors hover:text-foreground"
              >
                EULA
              </a>
              <a
                href="https://scrambl.com/aup"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors hover:text-foreground"
              >
                Acceptable Use
              </a>
              <a
                href="https://scrambl.com/disclaimer"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors hover:text-foreground"
              >
                Disclaimer
              </a>
              <a
                href="/beta"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors hover:text-foreground"
              >
                Beta
              </a>
            </div>
          </div>
        </div>
      </aside>

      {/* Vertical seam — subtle */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-0 hidden h-full w-px bg-gradient-to-b from-transparent via-foreground/[0.06] to-transparent lg:block xl:left-[55%]"
      />

      {/* ───────────────── RIGHT — Form ───────────────── */}
      <main className="relative flex h-full flex-1 flex-col overflow-y-auto overflow-x-clip pb-[env(safe-area-inset-bottom)] lg:w-1/2 xl:w-[45%]">
        {/* Mobile ambient gradient */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 lg:hidden"
          style={{
            background: `
              radial-gradient(ellipse 90% 50% at 50% 0%, hsl(var(--brand-green) / 0.10), transparent 60%),
              radial-gradient(ellipse 80% 50% at 50% 100%, hsl(var(--brand-blue) / 0.06), transparent 60%)
            `,
          }}
        />

        {/* Top bar */}
        <div className="relative flex items-center justify-between px-6 pt-4 lg:px-8 lg:pt-5 xl:px-12 xl:pt-6">
          <a
            href="https://scrambl.com"
            className="transition-opacity hover:opacity-80 lg:hidden"
          >
            <Logo
              size={24}
              withText
              textClassName="text-xl font-light tracking-wide font-kronaone"
            />
          </a>
          <div className="hidden lg:block" />
          {showModeToggle && (
            <div className="flex h-10 items-center">
              <ModeToggle />
            </div>
          )}
        </div>

        {/* Form container */}
        <div className="relative flex flex-1 items-center justify-center px-4 py-8 lg:px-8 xl:px-16">
          <div className="w-full max-w-[420px]">
            {/* Header */}
            <div className="mb-8 text-center lg:text-left">
              {/* Mobile-only mascot */}
              <Mascot
                variant={content.mascotVariant}
                size="md"
                animate="float"
                className="mx-auto mb-4 lg:hidden"
              />
              <h1 className="mb-3 font-heading text-[2rem] font-semibold leading-[1.1] tracking-tight text-foreground md:text-[2.25rem]">
                {headline}
              </h1>
              <p className="text-[15px] leading-relaxed text-muted-foreground">
                {subheadline}
              </p>
            </div>

            {/* Form content */}
            {children}
          </div>
        </div>

        {/* Mobile footer */}
        <div className="relative p-4 text-center lg:hidden">
          <div className="mb-2 flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <a
              href="https://scrambl.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-foreground"
            >
              Terms
            </a>
            <span>&bull;</span>
            <a
              href="https://scrambl.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-foreground"
            >
              Privacy
            </a>
            <span>&bull;</span>
            <a
              href="https://scrambl.com/eula"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-foreground"
            >
              EULA
            </a>
            <span>&bull;</span>
            <a
              href="https://scrambl.com/aup"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-foreground"
            >
              Acceptable Use
            </a>
            <span>&bull;</span>
            <a
              href="https://scrambl.com/disclaimer"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-foreground"
            >
              Disclaimer
            </a>
            <span>&bull;</span>
            <a
              href="/beta"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-foreground"
            >
              Beta
            </a>
          </div>
          <p className="text-xs text-muted-foreground">
            &copy; {year} Scrambl, LLC
          </p>
        </div>
      </main>
    </div>
  )
}
